import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useGetIdentity, useTranslate } from '@refinedev/core';
import { App as AntdApp, Button, Segmented, Space, Table, Tag, Typography } from 'antd';
import { addDays } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  ResultCard,
  ToolAssignButton,
  ToolSection,
  fmtDay,
  fmtDayShort,
} from '../../components/ToolSection';
import { DateFnsDatePicker } from '../../components/Pickers';
import { IUser } from '../../providers';
import type { AssignPreset } from '../homework/AssignHomeworkDrawer';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import { usePickerLocale } from '../homework/usePickerLocale';
import {
  DEFAULT_PERIOD,
  HEALTHY_ADULT_ID,
  PERIODS,
  SET_FIELDS,
  TIMELINE_FIELDS,
  changeLine,
  colourOf,
  daysBetween,
  healthyAdultLineOfWeek,
  matrixColumns,
  modeName,
  oneDecimal,
  orderedMap,
  percentOf,
  periodModesOf,
  rangeVariables,
  stackBars,
  type ModeMapEntry,
  type Period,
  type SetRow,
  type TimelineRow,
  type TriggerRow,
} from './model';
import { ModesSetupDrawer } from './ModesSetupDrawer';

/**
 * E47 · ClientModeTimeline + ConfigureModeSet (with the client) + AssignHomework (mode check-ins) — "Client Hub — Modes".
 * Rebuilt 2026-10-22 (N-6; design PORTAL-SECTIONS-2026-10-22 §2.2), results first, like the app's My week: THE WHOLE PERIOD (one bar per
 * mode: the days it was here and its strongest rating), the triggers × modes matrix, the Healthy Adult by week, the day-by-day stacked
 * chart, and last the Mode map (the therapist's set-up) with "Set up the modes" — a side drawer — on it. ONE Assign in the header.
 * The period is a REAL query input (7 · 30 · 90 days · Custom from–to): changing it asks the API again.
 *
 * Visible through a relation with VIEW permission (the hub's verdict is the caller's — `show.tsx`). DISPLAY ONLY: no alert, no
 * score, no advice is derived from a mode, an intensity or a trigger.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };
type Tx = (key: string, options?: Record<string, unknown>) => string;

const customDefault = () => {
  const to = new Date();
  return { from: addDays(to, -29), to };
};

export const ClientModesTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as Tx;
  const pickerLocale = usePickerLocale();
  const { message } = AntdApp.useApp();
  const refreshClientPage = useRefreshClientPage();
  const { data: identity } = useGetIdentity<IUser>();
  const viewerId = identity?.db?._id ?? null;
  const [setupOpen, setSetupOpen] = useState(false);

  // the client's own time zone — their days are theirs
  const zoneQuery = useCustom<{ _id: string; timezone?: string | null }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'timezone'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const zoneSettled = zoneQuery.isFetched || zoneQuery.isError;
  const clientZone =
    zoneQuery.data?.data?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── the period: a real query input ────────────────────────────────────────────────────────
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [custom, setCustom] = useState(customDefault);
  const range = rangeVariables(period, custom);

  const timelineQuery = useCustom<TimelineRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientModeTimeline',
      fields: TIMELINE_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        ...(range && 'days' in range
          ? { days: { name: 'days', type: 'Int', value: range.days } }
          : {}),
        ...(range && 'from' in range
          ? {
              from: { name: 'from', type: 'String', value: range.from },
              to: { name: 'to', type: 'String', value: range.to },
            }
          : {}),
        timeZone: { name: 'timeZone', type: 'String', value: clientZone },
      },
    },
    queryOptions: {
      enabled: Boolean(clientId) && zoneSettled && range !== null,
      retry: false,
      keepPreviousData: true,
    },
  });
  const timeline = timelineQuery.data?.data;

  const setQuery = useCustom<SetRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientModeSet',
      fields: SET_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const currentSet = setQuery.data?.data;

  const map = useMemo(() => orderedMap(timeline?.modeMap ?? []), [timeline]);
  const order = useMemo(() => map.map((m) => m.modeId), [map]);
  const nameOf = (m: Pick<ModeMapEntry, 'modeId' | 'label'>) => modeName(m, t);
  const mapEntry = (id: string) => map.find((m) => m.modeId === id);
  const nameOfId = (id: string) => {
    const entry = mapEntry(id);
    return entry ? nameOf(entry) : nameOf({ modeId: id, label: null });
  };

  const bars = useMemo(
    () =>
      timeline
        ? stackBars(
            daysBetween(timeline.from, timeline.to),
            timeline.modeDays,
            order,
          )
        : null,
    [timeline, order],
  );
  const periodModes = useMemo(
    () => (timeline ? periodModesOf(timeline.modeDays, timeline.modeMap) : []),
    [timeline],
  );
  const matrix: TriggerRow[] = timeline?.triggerMatrix ?? [];
  const columns = matrixColumns(matrix, order);
  const lastChange = changeLine(
    timeline?.lastChange ?? currentSet?.lastChange,
    viewerId,
  );
  const lastChangeAt = (timeline?.lastChange ?? currentSet?.lastChange)
    ?.occurredAt;
  const lastChangeText = t(`modes.hub.lastChange.${lastChange.key}`, {
    name: lastChange.name,
    date: lastChangeAt ? fmtDay(lastChangeAt, locale) : '',
  });
  const haEntry = mapEntry(HEALTHY_ADULT_ID);

  const hasData = (timeline?.checkIns ?? 0) > 0;
  const loadingTimeline = timelineQuery.isFetching && !timeline;

  const preset: AssignPreset = useMemo(
    () => ({
      kind: 'tool',
      target: 'modeCheckIn',
      targets: ['modeCheckIn'],
      defaults: {
        modeCheckIn: {
          title: t(HOMEWORK_DEFAULT_TITLE_KEYS.modeCheckIn), // Q-ru-3 A: left as it is, no title is stored
          reminderPolicy: 'default',
          deadline: {
            shape: 'repeat',
            frequency: 'daily',
            hour: 9,
            minute: 0,
            startInDays: 1,
            count: 7,
            countMax: 28,
            countUnit: 'days',
          },
        },
      },
    }),
    [t],
  );

  const toolbar = (
    <>
      <Typography.Text type="secondary">
        {t('modes.hub.period.label')}
      </Typography.Text>
      <Segmented
        data-testid="modes-hub-period"
        onChange={(v) => {
          const next = v as Period;
          if (next === 'custom') setCustom(customDefault());
          setPeriod(next);
        }}
        options={PERIODS.map((p) => ({
          value: p,
          label: (
            <span data-testid={`modes-hub-period-${p}`}>
              {t(`modes.hub.period.${p === 'custom' ? 'custom' : `d${p}`}`)}
            </span>
          ),
        }))}
        value={period}
      />
      {period === 'custom' ? (
        <span data-testid="modes-hub-range">
          <DateFnsDatePicker.RangePicker
            allowClear={false}
            disabledDate={(d: Date) => d > new Date()}
            format="yyyy-MM-dd"
            locale={pickerLocale}
            onChange={(v: [Date | null, Date | null] | null) => {
              if (v?.[0] && v?.[1]) setCustom({ from: v[0], to: v[1] });
            }}
            placeholder={[
              t('modes.hub.period.from'),
              t('modes.hub.period.to'),
            ]}
            value={[custom.from, custom.to]}
          />
        </span>
      ) : null}
    </>
  );

  // ── cards ─────────────────────────────────────────────────────────────────────────────────
  const periodCard = timeline ? (
    <ResultCard
      testId="modes-hub-period-card"
      role="result"
      title={t('modes.hub.period.title')}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space size={[16, 4]} wrap>
          <Typography.Text data-testid="modes-hub-checkins-week">
            {t('modes.hub.counts.thisWeek', { count: timeline.checkInsThisWeek })}
          </Typography.Text>
          <Typography.Text data-testid="modes-hub-checkins-range">
            {t('modes.hub.counts.inRange', { count: timeline.checkIns })}
          </Typography.Text>
          <Typography.Text type="secondary" data-testid="modes-hub-range-shown">
            {t('modes.hub.period.range', {
              from: fmtDay(timeline.from, locale),
              to: fmtDay(timeline.to, locale),
            })}
          </Typography.Text>
        </Space>
        {!hasData ? (
          <Typography.Text type="secondary" data-testid="modes-hub-empty">
            {t('modes.hub.emptyPeriod', { name: clientName })}
          </Typography.Text>
        ) : (
          <div data-testid="modes-hub-bars">
            {periodModes.map((p) => {
              const off = !!p.mode.switchedOff;
              const colour = off ? '#bfbfbf' : p.colour;
              return (
                <div
                  key={p.mode.modeId}
                  data-testid={`modes-hub-bar-${p.mode.modeId}`}
                  data-switched-off={off ? 'true' : 'false'}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '2px 12px',
                    padding: '4px 0',
                    opacity: off ? 0.6 : 1,
                  }}
                >
                  <span
                    style={{
                      flex: '0 0 170px',
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: colour,
                        display: 'inline-block',
                        flex: '0 0 auto',
                      }}
                    />
                    <Typography.Text ellipsis>
                      {nameOf(p.mode)}
                    </Typography.Text>
                    {off ? (
                      <Tag style={{ margin: 0 }}>
                        {t('modes.hub.map.switchedOff')}
                      </Tag>
                    ) : null}
                  </span>
                  <span
                    style={{
                      flex: '1 1 160px',
                      height: 12,
                      background: '#f0f0f0',
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      data-testid={`modes-hub-bar-fill-${p.mode.modeId}`}
                      data-width={p.widthPct}
                      style={{
                        display: 'block',
                        height: '100%',
                        width: `${p.widthPct}%`,
                        background: colour,
                      }}
                    />
                  </span>
                  <Typography.Text
                    type="secondary"
                    data-testid={`modes-hub-bar-meta-${p.mode.modeId}`}
                    style={{ flex: '0 0 auto' }}
                  >
                    {t('modes.hub.period.meta', {
                      days: p.days,
                      peak: p.peak,
                    })}
                  </Typography.Text>
                </div>
              );
            })}
          </div>
        )}
      </Space>
    </ResultCard>
  ) : null;

  const matrixCard =
    timeline && hasData ? (
      <ResultCard
        testId="modes-hub-matrix-card"
        role="result"
        title={t('modes.hub.matrix.title')}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          {t('modes.hub.matrix.hint')}
        </Typography.Paragraph>
        {matrix.length === 0 ? (
          <Typography.Text type="secondary" data-testid="modes-hub-matrix-empty">
            {t('modes.hub.matrix.empty')}
          </Typography.Text>
        ) : (
          <Table
            columns={[
              {
                title: t('modes.hub.matrix.trigger'),
                dataIndex: 'trigger',
                render: (v: string) => (
                  <span data-testid="modes-hub-matrix-trigger">{v}</span>
                ),
              },
              ...columns.map((id) => ({
                title: nameOfId(id),
                key: id,
                align: 'center' as const,
                render: (_: unknown, row: TriggerRow) => {
                  const n = row.modes.find((m) => m.modeId === id)?.count;
                  return (
                    <span data-testid={`modes-hub-matrix-cell-${id}`}>
                      {n ? n : '—'}
                    </span>
                  );
                },
              })),
            ]}
            data-testid="modes-hub-matrix"
            dataSource={matrix}
            pagination={false}
            rowKey="trigger"
            scroll={{ x: 'max-content' }}
            size="small"
          />
        )}
      </ResultCard>
    ) : null;

  const haCard =
    timeline && hasData ? (
      <ResultCard
        testId="modes-hub-ha"
        role="result"
        title={t('modes.hub.ha.title', {
          mode: haEntry ? nameOf(haEntry) : t('modes.catalog.ha.name'),
        })}
      >
        {timeline.healthyAdultShare.length === 0 ? (
          <Typography.Text type="secondary">
            {t('modes.hub.ha.empty')}
          </Typography.Text>
        ) : (
          <Space direction="vertical" size={2}>
            {timeline.healthyAdultShare.map((w) => {
              const line = healthyAdultLineOfWeek(w);
              const percent = percentOf(w.share);
              return (
                <Typography.Text
                  key={w.week}
                  data-testid={`modes-hub-ha-week-${w.week}`}
                >
                  <strong>
                    {t('modes.hub.ha.week', { date: fmtDayShort(w.week, locale) })}
                  </strong>
                  {' · '}
                  <span data-testid={`modes-hub-ha-share-${w.week}`}>
                    {line?.notRated
                      ? t('modes.hub.ha.notRated')
                      : percent === null
                      ? t('modes.hub.ha.noFigure')
                      : t('modes.hub.ha.share', { percent })}
                  </span>
                  {line?.notRated
                    ? ''
                    : ` · ${t('modes.hub.ha.present', {
                        present: w.presentCheckIns,
                        total: w.checkIns,
                      })}`}
                  {!line?.notRated && w.mean !== null && w.mean !== undefined
                    ? ` · ${t('modes.hub.ha.mean', { mean: oneDecimal(w.mean) })}`
                    : ''}
                </Typography.Text>
              );
            })}
          </Space>
        )}
      </ResultCard>
    ) : null;

  const timelineCard =
    timeline && bars && hasData ? (
      <ResultCard
        testId="modes-hub-timeline-card"
        role="result"
        title={t('modes.hub.timeline.title')}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          {t('modes.hub.timeline.hint')}
        </Typography.Paragraph>
        <svg
          aria-label={t('modes.hub.timeline.title')}
          data-from={timeline.from}
          data-testid="modes-hub-timeline"
          data-to={timeline.to}
          preserveAspectRatio="none"
          role="img"
          style={{ width: '100%', height: 160, display: 'block' }}
          viewBox={`0 0 ${Math.max(1, bars.bars.length) * 10} 100`}
        >
          <line
            x1={0}
            x2={bars.bars.length * 10}
            y1={100}
            y2={100}
            stroke="#d9d9d9"
            strokeWidth={0.5}
          />
          {bars.bars.map((bar, i) => (
            <g
              data-date={bar.date}
              data-testid={`modes-hub-timeline-day-${bar.date}`}
              key={bar.date}
            >
              {bar.segments.map((seg) => {
                const entry = mapEntry(seg.modeId);
                return (
                  <rect
                    data-mode={seg.modeId}
                    data-peak={seg.peak}
                    data-testid={`modes-hub-timeline-seg-${bar.date}-${seg.modeId}`}
                    fill={entry ? colourOf(entry) : '#bfbfbf'}
                    height={seg.height * 100}
                    key={seg.modeId}
                    opacity={entry?.switchedOff ? 0.45 : 1}
                    width={8}
                    x={i * 10 + 1}
                    y={100 - (seg.y + seg.height) * 100}
                  >
                    <title>
                      {`${fmtDayShort(bar.date, locale)} · ${t(
                        'modes.hub.timeline.segment',
                        { mode: nameOfId(seg.modeId), peak: seg.peak },
                      )}`}
                    </title>
                  </rect>
                );
              })}
            </g>
          ))}
        </svg>
        <Space
          size={[12, 4]}
          wrap
          style={{ marginTop: 8 }}
          data-testid="modes-hub-timeline-legend"
        >
          {map.map((m) => (
            <Space key={m.modeId} size={4}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: colourOf(m),
                  display: 'inline-block',
                  opacity: m.switchedOff ? 0.45 : 1,
                }}
              />
              <Typography.Text type="secondary">{nameOf(m)}</Typography.Text>
            </Space>
          ))}
        </Space>
      </ResultCard>
    ) : null;

  const mapCard = timeline ? (
    <ResultCard
      testId="modes-hub-map"
      role="config"
      title={t('modes.hub.map.title')}
      actions={
        <Button
          onClick={() => setSetupOpen(true)}
          data-testid="modes-setup-open"
        >
          {t('modes.hub.setup.open')}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        <span data-testid="modes-hub-last-change">{lastChangeText}</span>
      </Typography.Paragraph>
      <Space size={[8, 8]} wrap>
        {map.map((m) => (
          <div
            key={m.modeId}
            data-testid={`modes-hub-mode-${m.modeId}`}
            data-switched-off={m.switchedOff ? 'true' : 'false'}
            style={{
              border: `1px solid ${m.switchedOff ? '#d9d9d9' : colourOf(m)}`,
              borderLeft: `6px solid ${m.switchedOff ? '#bfbfbf' : colourOf(m)}`,
              borderRadius: 6,
              padding: '4px 10px',
              maxWidth: 320,
              opacity: m.switchedOff ? 0.55 : 1,
              filter: m.switchedOff ? 'grayscale(1)' : undefined,
            }}
          >
            <Space size={6} wrap>
              <Typography.Text strong>{nameOf(m)}</Typography.Text>
              <Tag style={{ margin: 0 }}>
                {t(`modes.catalog.kinds.${m.kind}`)}
              </Tag>
              {m.switchedOff ? (
                <Tag
                  style={{ margin: 0 }}
                  data-testid={`modes-hub-switched-off-${m.modeId}`}
                >
                  {t('modes.hub.map.switchedOff')}
                </Tag>
              ) : null}
              {m.cardId ? (
                <Tag
                  color="green"
                  style={{ margin: 0 }}
                  data-testid={`modes-hub-card-${m.modeId}`}
                >
                  {t('modes.hub.map.card')} ✓
                </Tag>
              ) : null}
            </Space>
            {m.cardText ? (
              <Typography.Paragraph
                style={{ margin: '2px 0 0' }}
                type="secondary"
                data-testid={`modes-hub-card-text-${m.modeId}`}
              >
                {m.cardText}
              </Typography.Paragraph>
            ) : null}
          </div>
        ))}
      </Space>
    </ResultCard>
  ) : null;

  return (
    <>
      <ToolSection
        testId="modes-hub-tab"
        title={t('modes.hub.title')}
        purpose={t('modes.hub.displayOnly')}
        toolbar={toolbar}
        actions={
          <ToolAssignButton
            clientId={clientId}
            clientName={clientName}
            preset={preset}
          />
        }
        loadError={timelineQuery.isError ? t('modes.hub.loadError') : null}
        loadErrorTestId="modes-hub-error"
      >
        {loadingTimeline ? (
          <Typography.Text type="secondary" data-testid="modes-hub-loading">
            {t('modes.hub.loading')}
          </Typography.Text>
        ) : null}
        {periodCard}
        {matrixCard || haCard ? (
          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
              alignItems: 'start',
            }}
          >
            {matrixCard}
            {haCard}
          </div>
        ) : null}
        {timelineCard}
        {mapCard}
      </ToolSection>

      <ModesSetupDrawer
        open={setupOpen}
        clientId={clientId}
        clientName={clientName}
        setMap={currentSet?.modeMap}
        knownMap={timeline?.modeMap ?? []}
        lastChangeText={lastChangeText}
        onClose={() => setSetupOpen(false)}
        onSaved={() => {
          setSetupOpen(false);
          message.success(t('modes.hub.setup.saved', { name: clientName }));
          refreshClientPage('assign');
          setQuery.refetch();
          timelineQuery.refetch();
        }}
      />
    </>
  );
};
